import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import "../supplier/supplierManagementTables.css";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import { sweetAlert } from "../../../../utils/UniversalFunction";
import {
  tableCardSurface,
  thStyle,
  secondaryBtn,
  statusBadge,
} from "./assessmentStyles";
import AssessmentFilterBar from "./AssessmentFilterBar";
import AssessmentEmptyState from "./AssessmentEmptyState";
import { applyAssessmentFilters } from "./assessmentFilters";

const ACTIVE_STATUSES = ["ASSIGNED", "IN_PROGRESS"];
const PAST_STATUSES = ["SUBMITTED", "REVIEWED", "CANCELLED"];

const STATUS_VARIANT = {
  ASSIGNED: "info",
  IN_PROGRESS: "warning",
  SUBMITTED: "success",
  REVIEWED: "success",
  CANCELLED: "muted",
};

const AssignmentList = ({ mode }) => {
  const { companyId: supplierId } = useParams();
  const location = useLocation();

  const [rows, setRows] = useState([]);
  const [assessmentTitles, setAssessmentTitles] = useState({});
  const [supplierNames, setSupplierNames] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState([]);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [cancellingId, setCancellingId] = useState(null);

  const statuses = mode === "past" ? PAST_STATUSES : ACTIVE_STATUSES;
  const title = mode === "past" ? "Past Assessments" : "Active Assessments";

  const isCancellable = (statusKey) =>
    statusKey === "ASSIGNED" || statusKey === "IN_PROGRESS";

  const handleCancel = async (row) => {
    if (!row?.id || !isCancellable(row.status_key)) return;
    const label =
      assessmentTitles[row.assessment_id] || `Assessment #${row.assessment_id}`;
    const supplierLabel =
      supplierNames[row.supplier_id] || `supplier #${row.supplier_id}`;
    const { isConfirmed } = await Swal.fire({
      title: "Cancel this assignment?",
      html: `<div style="text-align:left"><b>${label}</b> for <b>${supplierLabel}</b>.<br/><br/>The supplier will be notified by email and can no longer edit their response.</div>`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Cancel assignment",
      confirmButtonColor: "#b91c1c",
      cancelButtonText: "Keep it",
    });
    if (!isConfirmed) return;
    setCancellingId(row.id);
    const res = await AssessmentService.cancelAssignment(row.id);
    setCancellingId(null);
    if (!res?.isSuccess) return;
    sweetAlert("success", "Assignment cancelled.");
    setRows((prev) => prev.filter((r) => r.id !== row.id));
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      const [aRes, asmtRes, supRes] = await Promise.all([
        AssessmentService.listBySupplier(supplierId, { limit: 200 }),
        AssessmentService.listAssessments({ limit: 200 }),
        AssessmentService.listSuppliers(),
      ]);
      setLoading(false);
      if (aRes?.isSuccess) {
        const items = aRes?.data?.data?.items ?? [];
        setRows(items.filter((r) => statuses.includes(r.status_key)));
      }
      if (asmtRes?.isSuccess) {
        const items = asmtRes?.data?.data?.items ?? [];
        const map = {};
        items.forEach((a) => {
          map[a.id] = a.title;
        });
        setAssessmentTitles(map);
      }
      if (supRes?.isSuccess) {
        const items = supRes?.data?.data ?? [];
        const map = {};
        (Array.isArray(items) ? items : []).forEach((s) => {
          map[s.id] =
            s.company_name ||
            [s.first_name, s.last_name].filter(Boolean).join(" ") ||
            s.email ||
            `supplier-${s.id}`;
        });
        setSupplierNames(map);
      }
    })();
  }, [mode, supplierId]); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(
    () =>
      applyAssessmentFilters(rows, {
        search,
        statusFilter,
        statusField: "status_key",
        dateRange,
        dateField: "assigned_at",
        rowExtras: (r) => ({
          assessmentTitle: assessmentTitles[r.assessment_id],
          supplierName: supplierNames[r.supplier_id],
        }),
      }),
    [
      rows,
      search,
      statusFilter,
      dateRange,
      assessmentTitles,
      supplierNames,
    ]
  );

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle={title}
      totalCount={filtered.length}
    >
      <AssessmentFilterBar
        rows={rows}
        statusField="status_key"
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search assessment, supplier…"
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        dateRange={dateRange}
        onDateRangeChange={setDateRange}
        dateLabel="Assigned"
      />

      <div className="supplier-mgmt-table-wrap" style={tableCardSurface}>
        <div className="supplier-mgmt-table-scroll">
          <table className="supplier-mgmt-table table">
            <thead>
              <tr>
                <th style={thStyle}>#</th>
                <th style={thStyle}>Assessment</th>
                <th style={thStyle}>Supplier</th>
                <th style={{ ...thStyle, width: 110 }}>Status</th>
                <th style={{ ...thStyle, width: 120 }}>Assigned</th>
                <th style={{ ...thStyle, width: 120 }}>Due</th>
                <th
                  style={{ ...thStyle, width: 90, textAlign: "right" }}
                ></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    Loading…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ padding: 0 }}>
                    <AssessmentEmptyState
                      title={`No ${mode} assignments`}
                      message="Try adjusting the filters or search term."
                      hasFilters={
                        Boolean(search) ||
                        statusFilter.length > 0 ||
                        Boolean(dateRange.from || dateRange.to)
                      }
                      onClearFilters={() => {
                        setSearch("");
                        setStatusFilter([]);
                        setDateRange({ from: "", to: "" });
                      }}
                    />
                  </td>
                </tr>
              ) : (
                filtered.map((r, idx) => (
                  <tr key={r.id}>
                    <td>{idx + 1}</td>
                    <td>
                      <Link to={`/assessment/assignment/${r.id}`}>
                        {assessmentTitles[r.assessment_id] ||
                          `Assessment #${r.assessment_id}`}
                      </Link>
                    </td>
                    <td>
                      {supplierNames[r.supplier_id] ||
                        `Supplier #${r.supplier_id}`}
                    </td>
                    <td>
                      <span
                        style={statusBadge(
                          STATUS_VARIANT[r.status_key] || "info"
                        )}
                      >
                        {r.status_key}
                      </span>
                    </td>
                    <td>{r.assigned_at?.slice(0, 10)}</td>
                    <td>{r.due_at ? r.due_at.slice(0, 10) : "—"}</td>
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <Link
                        to={`/assessment/assignment/${r.id}`}
                        style={{
                          ...secondaryBtn,
                          height: 28,
                          padding: "0 10px",
                          fontSize: 12,
                          display: "inline-block",
                          lineHeight: "26px",
                          textDecoration: "none",
                        }}
                      >
                        View
                      </Link>
                      {isCancellable(r.status_key) && (
                        <button
                          type="button"
                          onClick={() => handleCancel(r)}
                          disabled={cancellingId === r.id}
                          style={{
                            ...secondaryBtn,
                            height: 28,
                            padding: "0 10px",
                            fontSize: 12,
                            marginLeft: 6,
                            color: "#b91c1c",
                            borderColor: "#fecaca",
                            background: "#fff",
                          }}
                        >
                          {cancellingId === r.id ? "Cancelling…" : "Cancel"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </SupplierManagementShell>
  );
};

export default AssignmentList;
